/* ++++++++++ --------------- IMPORTS --------------- ++++++++++ */
// libraries
import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
// utils
// material
import { Button, displayModal } from '@insticator/insticator-ui';
import Space from 'material/space/space'
// content
// styles
import injectSheet from "react-jss";
import styles from './buttonconfigmodal_styles';
import PreviewConfig from 'material/previewconfig/previewconfig';
import { ColorPicker, InlineMessage, SingleSelectDropDown, Switch, TextField } from '@insticator/insticator-ui/dist/material';
import { createThemeColors, getHueFromHex } from 'utils/theme';
import { ReactComponent as ComposeIcon } from 'assets/icons/compose.svg'

import { ReactComponent as ButtonIcon1 } from 'assets/icons/product-layout/buttonicons/icon_style_1.svg'
import { ReactComponent as ButtonIcon2 } from 'assets/icons/product-layout/buttonicons/icon_style_2.svg'
import { ReactComponent as ButtonIcon3 } from 'assets/icons/product-layout/buttonicons/icon_style_3.svg'
import { ReactComponent as ButtonIcon4 } from 'assets/icons/product-layout/buttonicons/icon_style_4.svg'
import { ReactComponent as ButtonIcon5 } from 'assets/icons/product-layout/buttonicons/icon_style_5.svg'
import { ReactComponent as NoneIcon } from 'assets/icons/product-layout/buttonicons/icon_style_none.svg'


const icons = {
  COMMENT: <ButtonIcon1 />,
  COMMENT_FILLED: <ButtonIcon2 />,
  SMILE: <ButtonIcon3 />,
  SMILE_FILLED: <ButtonIcon4 />,
  UP_ARROW: <ButtonIcon5 />,
  NONE: ''
}

const buttonSizeMap = {
  SMALL: {value: 'SMALL', name: 'Small'},
  MEDIUM: {value: 'MEDIUM', name: 'Medium'},
  LARGE: {value: 'LARGE', name: 'Large'}
}


const buttonSizeOptions = [buttonSizeMap.SMALL, buttonSizeMap.MEDIUM, buttonSizeMap.LARGE]

const typefaceMap = {
  DECORATIVE: { name: 'Decorative', value: 'DECORATIVE'},
  PLAIN: { name: 'Plain', value: 'PLAIN'},
}

const typefaceOptions = [typefaceMap.DECORATIVE, typefaceMap.PLAIN]


const alignmentMap = {
  LEFT: { name: 'Left', value: 'LEFT'},
  CENTER: { name: 'Center', value: 'CENTER'},
  RIGHT: { name: 'Right', value: 'RIGHT'}
}

const alignmentOptions = [alignmentMap.LEFT, alignmentMap.CENTER, alignmentMap.RIGHT]


/* ========== ~~~~~~~~~~ CONTROL BAR MODAL ~~~~~~~~~~ ========== */
const ButtonConfigModal  = injectSheet(styles)((props) => {
  const dispatch = useDispatch();

  const { 
      primaryColor =  '#061240',
      enableMonochrome = false, 
      text = 'View Comments',
      iconStyle = 'COMMENT',
      fontTypeface = 'PLAIN', 
      alignment = 'LEFT', 
      buttonSize = 'MEDIUM', 
      onSave,
      onCancel 
    } = props

  const [isProcessing, setIsProcessing] = useState(false)

  const [isMonochrome, setIsMonochrome] = useState(enableMonochrome)
  const [customPrimaryColor, ] = useState(primaryColor)
  const [tempPrimaryColor, setTempPrimaryColor] = useState(primaryColor)


  const [tempButtonText, setTempButtonText] = useState(text)
  const [tempButtonSize, setTempButtonSize] = useState(buttonSize)
  const [tempButtonIcon, setTempButtonIcon] = useState(iconStyle)
  const [tempFontTypeface, setTempFontTypeface] = useState(fontTypeface)
  const [tempAlignment, setTempAlignment] = useState(alignment)


  const [colorSchemaValid, setColorSchemaValid] = useState({
     customPrimaryColor: true
})
  const handleCancel = () => {
    dispatch(displayModal(false))
    onCancel?.()
  }


  const handleChange = (property,  value) => {
    if(property === 'text') setTempButtonText(value)
    if(property === 'buttonSize') setTempButtonSize(value)
    if(property === 'fontTypeface') setTempFontTypeface(value)
    if(property === 'alignment') setTempAlignment(value)
  }

  const handleSaveControlBarConfig = () => {
    setIsProcessing(true)
    onSave?.({
      primaryColor: tempPrimaryColor,
      enableMonochrome: isMonochrome,
      text: tempButtonText,
      iconStyle: tempButtonIcon,
      fontTypeface: tempFontTypeface,
      alignment: tempAlignment,
      buttonSize: tempButtonSize
    })
    setIsProcessing(false)
  }


  const handleMonochromeChange = (isMonochrome) => {
    setIsMonochrome(isMonochrome)
  }

  const handlePrimaryColorChange =  (color) => {
    setTempPrimaryColor(color)
  }

  const validateColor = (isValid) => {
    setColorSchemaValid({
      ...colorSchemaValid,
      'customPrimaryColor': isValid
    })
  }

  const resetColor = () => {
    setTempPrimaryColor(customPrimaryColor)
  }

  const handleButtonIconChange = (icon) => {
    setTempButtonIcon(icon)
  }

  const themeColors = useMemo(() => createThemeColors(false, getHueFromHex(tempPrimaryColor), !!isMonochrome), [tempPrimaryColor, isMonochrome])

  return (
    <div className={'buttonconfigmodal'}>
      <h4>Button Configurations</h4>
      <Space gap={24} direction={'row'}>
      <Space gap={16} direction={'column'}> 
        <Space gap={16} direction={'column'} klass={`primarycolor disabled-${!!isMonochrome}`}>
          <ColorPicker 
            valid={true}
            color={tempPrimaryColor}
            savedColor={tempPrimaryColor}
            label={'Primary Color'}
            handleColorChange={(color, e) => handlePrimaryColorChange(color)}
            processColorUpdate={(color, e) => handlePrimaryColorChange(color)}
            validateColor={(isValid, e) => validateColor(isValid, 'customPrimaryColor')}
            handleReset={() => resetColor('customPrimaryColor')} 
          />
          <Space klass={'usemonochrome'} gap={8} alignItems={'center'}>
            <Switch 
              on={!!isMonochrome} 
              handleClick={() => handleMonochromeChange(!isMonochrome)} 
            />
            <span>Monochrome</span>
          </Space>
        </Space>

        <Space klass={'headericons'}>
          <div className={`icon selected-${tempButtonIcon === 'COMMENT'}`} onClick={() => handleButtonIconChange('COMMENT')}>
            {icons['COMMENT']}
          </div>
          <div className={`icon selected-${tempButtonIcon === 'COMMENT_FILLED'}`} onClick={() => handleButtonIconChange('COMMENT_FILLED')}>
            {icons['COMMENT_FILLED']}
          </div>
          <div className={`icon selected-${tempButtonIcon === 'SMILE'}`} onClick={() => handleButtonIconChange('SMILE')}>
            {icons['SMILE']}
          </div>
         
        </Space>

        <Space klass={'headericons'}>
          <div className={`icon selected-${tempButtonIcon === 'SMILE_FILLED'}`} onClick={() => handleButtonIconChange('SMILE_FILLED')}>
            {icons['SMILE_FILLED']}
          </div>
          <div className={`icon selected-${tempButtonIcon === 'UP_ARROW'}`} onClick={() => handleButtonIconChange('UP_ARROW')}>
            {icons['UP_ARROW']}
          </div>
          <div className={`icon none selected-${tempButtonIcon === 'NONE'}`} onClick={() => handleButtonIconChange('NONE')}>
            <NoneIcon />
          </div>
        </Space>

        <TextField 
          type={`text`} 
          name={`text`} 
          icon={<ComposeIcon height={18} width={18} />}
          value={tempButtonText} 
          handleChange={e => handleChange('text', e.target.value)}
          placeholder={``} 
          required
          valid={!!tempButtonText}
          label={'Text'}
        />

        <SingleSelectDropDown 
          name={buttonSizeMap[tempButtonSize].name} 
          searchable={false} 
          onSelect={opt =>  handleChange('buttonSize', opt.value)} 
          items={buttonSizeOptions}
          label={'Button size'}
        />

        <SingleSelectDropDown 
          name={typefaceMap[tempFontTypeface].name} 
          searchable={false} 
          onSelect={opt =>  handleChange('fontTypeface', opt.value)} 
          items={typefaceOptions}
          label={'Typeface'}
        />

        <SingleSelectDropDown 
          name={alignmentMap[tempAlignment].name} 
          searchable={false} 
          onSelect={opt =>  handleChange('alignment', opt.value)} 
          items={alignmentOptions}
          label={'Alignment'}
        />
        
      </Space>
      <PreviewConfig>
        <div className={`buttonpreviewcontainer ${tempAlignment}`}>
          <div className={`buttonpreview ${tempButtonSize} ${tempFontTypeface}`} style={{background: themeColors['primary']}}>
            {!!iconStyle ? icons[tempButtonIcon] : ''}
            {tempButtonText}
          </div>
        </div>
      </PreviewConfig>
    </Space>
      <Space style={{marginTop: '70px'}} alignItems={'center'}>
        <Button text='Cancel' variant='none' color='light' handleClick={handleCancel} />
        <Button 
          text='Save' 
          variant='primary' 
          disabled={isProcessing} 
          processingText={'Saving Configurations'} 
          processing={isProcessing} 
          handleClick={() => handleSaveControlBarConfig()} 
        />
      </Space>
    </div>
  )
})

/* ++++++++++ --------------- EXPORTS --------------- ++++++++++ */
export default ButtonConfigModal